import { useId, useState, type ChangeEvent, type FormEvent } from 'react'
import { Check, Loader2, Paperclip, Star, X } from 'lucide-react'
import { api } from '@/lib/api'
import { buildFeedbackClientContext } from '@/lib/feedbackClientContext'
import {
  ACCEPT_ATTACHMENT_ATTR,
  isAllowedAttachmentFile,
  MAX_ATTACHMENT_BYTES,
} from '@/lib/attachmentRules'
import { formatUsPhoneE164, isValidUsPhone } from '@/lib/usPhone'
import { storeForm } from '@/components/store/storeFormStyles'

const MIN_DESCRIPTION_LEN = 10

/** Shown on star hover (tooltip) and next to the selected rating. */
const RATING_MOOD: Record<number, string> = {
  1: 'Bad',
  2: 'Not great',
  3: 'Okay',
  4: 'Great',
  5: 'Excellent',
}

/** Feedback-only Mr. Sai poses; same mascot design as hero tiles (young clerk, green apron with S). */
const MRSAI_FEEDBACK_IMAGE = {
  grateful: { src: '/mrsai-feedback-excited.png', alt: 'Mr. Sai celebrating your great rating' },
  empathy: { src: '/mrsai-feedback-empathy.png', alt: 'Mr. Sai listening with care' },
} as const

/** One short line below the main thank-you (above 3 stars vs 3 or below). */
function thankYouSubline(rating: number): string {
  if (rating > 3) {
    if (rating >= 5) {
      return "We're thrilled you had a great experience — thank you!"
    }
    return "We're glad things went well. Thanks again!"
  }
  return "We hear you, and we'll use your feedback to improve."
}

export default function ShareExperienceForm() {
  const attachmentInputId = useId()
  const [rating, setRating] = useState<number | null>(null)
  const [topic, setTopic] = useState<'store' | 'web' | null>(null)
  const [description, setDescription] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [attachment, setAttachment] = useState<File | null>(null)
  const [attachmentFileKey, setAttachmentFileKey] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const [submittedRating, setSubmittedRating] = useState<number | null>(null)

  const descLen = description.trim().length
  const descOk = descLen >= MIN_DESCRIPTION_LEN
  const phoneOk = isValidUsPhone(phone)
  const canSubmit = rating != null && topic != null && descOk && phoneOk && !submitting

  function onAttachmentChange(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (!f) {
      setAttachment(null)
      return
    }
    if (f.size > MAX_ATTACHMENT_BYTES) {
      setAttachment(null)
      setAttachmentFileKey((k) => k + 1)
      setError(`Attachment must be ${MAX_ATTACHMENT_BYTES / 1024} KB or smaller.`)
      return
    }
    if (!isAllowedAttachmentFile(f)) {
      setAttachment(null)
      setAttachmentFileKey((k) => k + 1)
      return
    }
    setAttachment(f)
    setError(null)
  }

  function clearAttachment() {
    setAttachment(null)
    setAttachmentFileKey((k) => k + 1)
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (rating == null) {
      setError('Please tap a star to rate your experience.')
      return
    }
    if (topic == null) {
      setError('Please choose store feedback or web feedback.')
      return
    }
    if (!descOk) {
      setError(`Tell us a bit more — at least ${MIN_DESCRIPTION_LEN} characters in the description.`)
      return
    }
    if (!isValidUsPhone(phone)) {
      setError('Please enter a valid US phone number (10 digits, including area code), or leave it blank.')
      return
    }
    setSubmitting(true)
    try {
      const client_context = await buildFeedbackClientContext()
      const payload = {
        rating,
        topic,
        description: description.trim(),
        client_context,
        ...(email.trim() ? { email: email.trim() } : {}),
        ...(phone.trim() ? { phone: formatUsPhoneE164(phone) } : {}),
      }
      const res = await api.storefront.submitFeedback(payload, attachment)
      if (res.success) {
        setSubmittedRating(rating)
        setSubmitted(true)
        setAttachment(null)
        setAttachmentFileKey((k) => k + 1)
      } else {
        setError('We could not save your feedback. Please try again.')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted && submittedRating != null) {
    const isPositive = submittedRating > 3
    const { src, alt } = isPositive ? MRSAI_FEEDBACK_IMAGE.grateful : MRSAI_FEEDBACK_IMAGE.empathy
    return (
      <div
        className={storeForm.body}
        role="status"
        aria-live="polite"
      >
        <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start sm:gap-8 sm:text-left">
          <img
            src={src}
            alt={alt}
            className="h-28 w-auto max-h-48 shrink-0 object-contain drop-shadow-sm sm:h-44"
            width={176}
            height={176}
            decoding="async"
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start sm:gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 sm:h-10 sm:w-10">
                <Check className="h-4 w-4 sm:h-5 sm:w-5" strokeWidth={2.5} aria-hidden />
              </div>
              <h2 id="share-experience-title" className="text-center text-base font-semibold leading-snug text-stone-900 sm:text-left sm:text-lg">
                Thanks for taking your time to write this valuable feedback.
              </h2>
            </div>
            <p className="mt-2 text-center text-xs leading-relaxed text-stone-600 sm:text-left sm:text-sm">
              {thankYouSubline(submittedRating)}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={storeForm.body}>
      <h2 id="share-experience-title" className={storeForm.title}>Share your experience</h2>

      <form onSubmit={handleSubmit} className={storeForm.form} noValidate>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:items-start sm:gap-8">
          <div className="min-w-0" role="group" aria-labelledby="feedback-rate-label">
            <p id="feedback-rate-label" className={storeForm.sectionLabel}>
              Rate your experience <span className="font-normal text-red-600">*</span>
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-0.5 sm:mt-2 sm:gap-2">
              {[1, 2, 3, 4, 5].map((n) => {
                const active = rating != null && n <= rating
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setRating(n)}
                    title={RATING_MOOD[n]}
                    className="rounded-lg p-1 text-amber-400 sm:p-1.5 transition hover:bg-amber-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
                    aria-label={`${n} out of 5 — ${RATING_MOOD[n]}${active ? ', selected' : ''}`}
                    aria-pressed={active}
                  >
                    <Star
                      className={`h-7 w-7 sm:h-10 sm:w-10 ${active ? 'fill-current' : 'fill-transparent'}`}
                      strokeWidth={active ? 0 : 1.5}
                    />
                  </button>
                )
              })}
              {rating != null ? (
                <span className="ml-1 text-xs font-medium text-stone-700 sm:text-sm">{RATING_MOOD[rating]}</span>
              ) : null}
            </div>
          </div>

          <div className="min-w-0" role="group" aria-labelledby="feedback-topic-label">
            <p id="feedback-topic-label" className={storeForm.sectionLabel}>
              Store feedback or web feedback? <span className="font-normal text-red-600">*</span>
            </p>
            <div className="mt-1.5 flex gap-2 sm:mt-2 sm:flex-wrap">
              <button
                type="button"
                onClick={() => setTopic('store')}
                className={`flex-1 rounded-full border px-3 py-1.5 text-center text-xs font-medium transition sm:flex-none sm:py-2 sm:text-sm sm:min-w-[11rem] ${
                  topic === 'store'
                    ? 'border-primary-500 bg-primary-50 text-primary-900'
                    : 'border-stone-200 bg-white text-stone-700 hover:border-stone-300'
                }`}
                aria-pressed={topic === 'store'}
              >
                Store feedback
              </button>
              <button
                type="button"
                onClick={() => setTopic('web')}
                className={`flex-1 rounded-full border px-3 py-1.5 text-center text-xs font-medium transition sm:flex-none sm:py-2 sm:text-sm sm:min-w-[11rem] ${
                  topic === 'web'
                    ? 'border-primary-500 bg-primary-50 text-primary-900'
                    : 'border-stone-200 bg-white text-stone-700 hover:border-stone-300'
                }`}
                aria-pressed={topic === 'web'}
              >
                Web feedback
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-1">
          <label htmlFor="feedback-description" className={storeForm.sectionLabel}>
            Description <span className="font-normal text-red-600">*</span>
          </label>
          <textarea
            id="feedback-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            maxLength={5000}
            required
            placeholder="What stood out — friendly staff, product selection, checkout, something we could improve? Share as much as you like."
            className={`resize-y ${storeForm.input} ${storeForm.inputOk}`}
          />
          <div className={`flex flex-wrap justify-between gap-1 ${storeForm.hint}`}>
            <span>
              {descLen < MIN_DESCRIPTION_LEN
                ? `${MIN_DESCRIPTION_LEN - descLen} more characters needed`
                : 'Looks good'}
            </span>
            <span className="tabular-nums">{descLen} / 5000</span>
          </div>
        </div>

        <div className="space-y-3 rounded-xl border border-stone-100 bg-stone-50/80 p-3 sm:space-y-4 sm:p-5">
          <div>
            <p className={storeForm.sectionLabel}>Contact (optional)</p>
            <p className={`mt-0.5 ${storeForm.hint}`}>
              Add email or phone only if you&apos;re open to a reply. We won&apos;t use it for marketing.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">
            <div className="space-y-1">
              <label htmlFor="feedback-email" className={storeForm.fieldLabel}>
                Email
              </label>
              <input
                id="feedback-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className={`${storeForm.input} ${storeForm.inputOk}`}
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="feedback-phone" className={storeForm.fieldLabel}>
                Phone (US)
              </label>
              <input
                id="feedback-phone"
                type="tel"
                autoComplete="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="e.g. (312) 588-2300"
                aria-invalid={phone.trim().length > 0 && !phoneOk}
                className={`${storeForm.input} ${phone.trim().length > 0 && !phoneOk ? storeForm.inputBad : storeForm.inputOk}`}
              />
              {phone.trim().length > 0 && !phoneOk ? (
                <p className="text-[11px] text-red-600 sm:text-xs">Enter a valid US mobile or landline number, or clear this field.</p>
              ) : (
                <p className={storeForm.hint}>Optional. 10-digit US numbers only.</p>
              )}
            </div>
          </div>

          <div className="space-y-1.5 border-t border-stone-200/80 pt-3 sm:space-y-2 sm:pt-4">
            <label htmlFor={attachmentInputId} className={`flex items-center gap-1.5 ${storeForm.fieldLabel}`}>
              <Paperclip className="h-3.5 w-3.5 text-stone-500" aria-hidden />
              Attachment (optional)
            </label>
            <p className={storeForm.hint}>
              One file, up to 500 KB — JPG, PNG, or PDF only. Other file types are ignored.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <input
                key={attachmentFileKey}
                id={attachmentInputId}
                type="file"
                accept={ACCEPT_ATTACHMENT_ATTR}
                onChange={onAttachmentChange}
                className={storeForm.file}
              />
              {attachment ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-stone-100 px-2 py-1 text-xs text-stone-700">
                  <span className="max-w-[200px] truncate">{attachment.name}</span>
                  <button
                    type="button"
                    onClick={clearAttachment}
                    className="rounded p-0.5 text-stone-500 hover:bg-stone-200 hover:text-stone-800"
                    aria-label="Remove attachment"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              ) : null}
            </div>
          </div>
        </div>

        {error ? (
          <p className={storeForm.error} role="alert">
            {error}
          </p>
        ) : null}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
          <p className={storeForm.hint}>
            By submitting, you agree we may use your feedback to improve our store and website.
          </p>
          <button
            type="submit"
            disabled={!canSubmit}
            className={storeForm.submit}
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                Sending…
              </>
            ) : (
              'Send feedback'
            )}
          </button>
        </div>
      </form>
    </div>
  )
}
